/*
 *
 * IndexChannels
 * */
import React, { useEffect, useRef } from "react";
import { connect } from "react-redux";
import withRouter from "withRouter";
import { toast } from "sonner";
import { t } from "copy/t";
import { createStructuredSelector } from "reselect";
import { isString, isObject } from "utils/helpers";
import {
  sortByPosition,
  resolveDisplayName,
  withId,
} from "containers/ChannelsPage/Forms/components/helpers";
import { selectChannelThemes, selectChannelThemeInstances } from "./selectors";
import {
  patchChannelRequest,
  fetchChannelDetailRequest,
  fetchUpdatedChannelDetailRequest,
} from "./actions";

import Forms from "./Forms";
import SubscriptionRequiredNotice from "components/SubscriptionRequiredNotice";
import ListError from "components/ListError";
import { fetchSession } from "containers/App/actions";
import {
  selectIsPhotographer,
  selectChannelManagementBlocked,
  selectSessionUserLoaded,
  selectSessionLoadFailed,
} from "containers/App/selectors";

import dayjs from "dayjs";
import Loader from "components/Loader";
import { colors } from "globalStyles";
import { useForm, useWatch } from "react-hook-form";
import { shareTypeFlag } from "./shareTypeFlag";

const urlFix = (url) => (url && url.slice(-11) !== "missing.png" ? url : null);
const makeUrl = (file) => window.URL.createObjectURL(file);
const getBrandFormData = (substate) => {
  const urlFix = (url) =>
    url && (isObject(url) || !url.includes("missing.png")) ? url : null;
  const keys = [
    "big_logo_image_url",
    "app_background_color",
    "overlay_image",
    "photo_filters",
    "gallery_banner",
    "header",
    "sub_header",
    "gallery_cta_text",
    "gallery_cta_link",
    "gallery_color",
    "app_background_color",
  ];
  var data = {};
  if (substate) {
    keys.map((key) => {
      if (
        ["big_logo_image_url", "overlay_image", "gallery_banner"].includes(key)
      ) {
        data[key] = urlFix(substate[key]);
      } else if (key === "photo_filters") {
        if (
          Array.isArray(substate[key]) &&
          !!substate[key][0] &&
          !!substate[key][0].file
        ) {
          data[key] = substate["photo_filters"].filter(
            (filter) => !!filter.file && filter.file.name !== null
          );
        }
      } else {
        data[key] = substate[key];
      }
    });
  }

  return data;
};
const getMessageFormData = (substate) => {
  const keys = [];
  var data = {};
  if (substate) {
    keys.map((key) => {
      data[key] = substate[key];
    });
  }

  return data;
};

// Map a channel's raw API photo_filters into the working form shape
// (`{ file: { name }, selected }`) — the exact shape
// ImageSelect.componentDidMount produces on load. Sorted by ascending
// `position` so the dashboard and the iPad agree on filter order. Used to
// re-seed the field from server truth after a save (see the effect below).
//
// Entries go through `withId` for the same reason componentDidMount does: the
// server `id` is the ONLY way a later deletion can be stated explicitly, and
// this re-seed replaces the working array after every save. Rebuilding
// without it would strip the ids the user's next delete needs, so the PATCH
// would carry the blank sentinel alone and the server would delete nothing —
// the filter would silently reappear until a full page reload.
export const reseedPhotoFilters = (rawFilters) => {
  const reseeded = [];
  sortByPosition(rawFilters || []).forEach((x) => {
    const name = resolveDisplayName(x);
    if (name) reseeded.push(withId({ file: { name }, selected: false }, x));
  });
  return reseeded;
};

const EditChannel = (props) => {
  const form = useForm({
    defaultValues: {
      with_photo_filter: true,
      with_photo_filter_graphic: true,
      twitter: true,
      email: true,
      hashtag: "",
      company_promo: "",
      app_background_color: "",
      hashtag_enabled: false,
      company_promo_enabled: false,
      email_required: false,
      phone_required: false,
      twitter_required: false,
      email_enabled: true,
      phone_enabled: true,
      twitter_enabled: true,
      big_logo_image_url: null,
      gallery_color: "",
      photo_filters: null,
      sub_header: "",
      header: "",
      cta_text_link: "",
      gallery_cta_text: "",
      gallery_cta_link: "",
      share_types: [
        { required: false, enabled: false, name: "twitter" },
        { required: false, enabled: false, name: "phone" },
        { required: false, enabled: false, name: "name" },
        { required: false, enabled: false, name: "zip code" },
        { required: false, enabled: false, name: "date of birth" },
        { required: false, enabled: false, name: "email" },
        { required: false, enabled: false, name: "gallery_email" },
        { required: false, enabled: false, name: "gallery_phone" },
        { required: false, enabled: false, name: "gallery_twitter" },
        { required: false, enabled: false, name: "gallery_facebook" },
        { required: false, enabled: false, name: "first_name" },
        { required: false, enabled: false, name: "last_name" },
      ]
    }
  });

  const {
    isFetching,
    channelDetail,
    channelDetailError,
    refreshPending,
    params: { channelId },
    channelThemes,
    channelThemeInstances,
    isPhotographer,
    patchChannel,
    fetchUpdatedDetail,
    isSaving,
    channelManagementBlocked,
    sessionUserLoaded,
    sessionLoadFailed,
    retrySession,
  } = props;

  useEffect(() => {
    const {
      channelDetail,
      fetchDetail,
      params: { channelId },
    } = props;
    if (!channelDetail || channelId) {
      fetchDetail(channelId);
    }
  }, []);

  useEffect(() => {
    if (!props.isSaving) {
      const {
        fetchUpdatedDetail,
        params: { channelId },
      } = props;
      fetchUpdatedDetail(channelId);
    }
  }, [channelId, isSaving]);

  // The photo_filters field is seeded once on mount (ImageSelect.componentDidMount)
  // and never re-seeded, so after a save it still holds the raw File objects from
  // the just-uploaded frames. On the next save those Files are re-sent as fresh
  // uploads, so the backend soft-deletes the prior filters and recreates them — a
  // new row every save (the "conveyor belt"). We re-seed from server truth once the
  // post-save refresh lands.
  //
  // The trigger is `refreshPending` going true -> false, which the reducer flips
  // for exactly one event: a successful detail response to a request issued
  // AFTER the save (matrix row 5). Keying off channelDetail's identity instead
  // looks equivalent and is not — channelDetail also changes for a response
  // issued BEFORE the save (row 6), whose payload predates the just-uploaded
  // frames, so re-seeding from it drops them from the next PATCH. Sharing one
  // predicate with the save guard is what keeps the two from drifting apart.
  const prevRefreshPendingRef = useRef(refreshPending);

  // The ids this editor last SUBMITTED for deletion — captured in submitChannel
  // (below) at the moment the PATCH goes out, not read here. Only THOSE ids are
  // safe to drop once the save is confirmed; anything the user deletes DURING
  // the outstanding refresh (BrandingForm's recordDeletedFilterId still runs —
  // saveBlocked only blocks the NEXT save, not editing) is a deletion the
  // server has never been told about, and clearing the whole field on refresh
  // would silently discard it (Greptile #532, P1). saveBlocked prevents a
  // second PATCH while one is outstanding, so exactly one snapshot is ever
  // in flight at a time.
  const submittedDeletedIdsRef = useRef([]);

  // Tradeoff, unchanged: this overwrites any filter edit made in the window
  // between save completion and the refresh landing — server truth wins after a
  // save. Only photo_filters is re-seeded, so no other in-progress field edit is
  // touched. `form` is a stable react-hook-form instance and is listed only to
  // satisfy exhaustive-deps.
  useEffect(() => {
    const refreshResolved = prevRefreshPendingRef.current && !refreshPending;
    prevRefreshPendingRef.current = refreshPending;
    if (refreshResolved && channelDetail) {
      form.setValue("photo_filters", reseedPhotoFilters(channelDetail.photo_filters));
      // The ids just PATCHed (the snapshot above) have been applied
      // server-side, so THOSE are stale. Remove only them — via set
      // difference, not a wipe to `[]` — so a deletion recorded WHILE this
      // refresh was outstanding (not part of the confirmed save) survives to
      // ride on the next PATCH instead of being silently dropped. This fires
      // only on the same successful post-save refresh as the re-seed above
      // (never on a failed save/refresh, which never resolves
      // `refreshPending` — see the matrix above), so a failed save keeps all
      // its ids for a retry. Re-sending an already-deleted id is a harmless
      // no-op server-side (movebooth-rails `explicitly_deleted_photo_filters`
      // treats it as already-owned), but pruning the confirmed ones here keeps
      // the wire payload from growing unbounded across saves.
      const confirmed = new Set(
        submittedDeletedIdsRef.current.map((id) => String(id)),
      );
      const current = form.getValues("deleted_photo_filter_ids");
      form.setValue(
        "deleted_photo_filter_ids",
        Array.isArray(current)
          ? current.filter((id) => !confirmed.has(String(id)))
          : [],
      );
    }
  }, [refreshPending, channelDetail, form]);

  // Until a refresh succeeds, the re-seed above has not run: photo_filters
  // still holds the raw File objects from the frames just uploaded. Saving from
  // that state re-uploads them, so the backend soft-deletes and recreates the
  // filters — the conveyor belt the re-seed exists to stop. So the editor
  // refuses to save for as long as the refresh is outstanding.
  //
  // `refreshPending` is the whole guard, and it is deliberately NOT the same
  // question as `channelDetailError`. Only a successful refresh clears it — a
  // retry merely being IN FLIGHT does not, or the save path would re-open for
  // the duration of every retry. The error is separate because it is only the
  // explanation: it says why the refresh is still outstanding, not whether it
  // is. Blocking is the visible outcome on purpose — a user told to reload is
  // strictly better off than one whose filters are silently duplicated.
  const saveBlocked = refreshPending;
  const refreshFailed = Boolean(channelDetailError);

  // Every form tab wires its <form> to `handleSubmit(onSubmit)` with this same
  // callback, so guarding here covers every save path — a disabled button on
  // one tab would not.
  const submitChannel = (data) => {
    if (saveBlocked) {
      toast.error(
        refreshFailed
          ? t("toast.channel.saveBlockedRefreshFailed")
          : t("toast.channel.saveBlockedRefreshing")
      );
      return;
    }
    // Snapshot exactly the ids THIS PATCH is telling the server to delete, so
    // the post-save refresh effect above clears only these once confirmed —
    // never a deletion recorded after this snapshot was taken (see that
    // effect's comment).
    submittedDeletedIdsRef.current = Array.isArray(data.deleted_photo_filter_ids)
      ? data.deleted_photo_filter_ids
      : [];
    patchChannel(data);
  };

  const refreshErrorBanner = refreshFailed ? (
    <div
      role="alert"
      data-testid="channel-refresh-error"
      className="mb-4 rounded border border-red-300 bg-red-50 p-4 text-sm text-red-800"
    >
      <p className="font-semibold">Couldn&rsquo;t refresh this channel</p>
      <p className="mt-1">
        {saveBlocked
          ? "Saving is paused so your photo filters aren’t duplicated. Retry below — saving resumes as soon as a refresh succeeds. Reloading the page always works too."
          : "You can keep working; this channel just didn’t reload."}
      </p>
      <button
        type="button"
        onClick={() => fetchUpdatedDetail(channelId)}
        className="mt-2 rounded border border-red-400 px-3 py-1 font-semibold hover:bg-red-100"
      >
        Retry
      </button>
    </div>
  ) : null;

  const values = useWatch({ control: form.control });
  const themeId = values["channel_theme_id"];
  const channelFormLogo = values["big_logo_image"];
  const channelFormBackground = values["app_background_color"];
  const galleryColor = values["gallery_color"];
  const themeInstanceId = values["channel_theme_instance"];
  const brandForm = getBrandFormData(values);
  const messageForm = getMessageFormData(values);

  // Asked before the gate below it. On a reload the session fetch has not
  // resolved yet, so the flag is absent for a reason the fail-open read cannot
  // see — "not asked yet" is not "the server said no". Rendering the editor in
  // that window hands a blocked user a live, interactive form for as long as
  // the request takes. Distinct from `isFetching`, which is the CHANNEL fetch:
  // this one is the SESSION, and it decides whether the gate can be trusted at
  // all.
  //
  // Both waiting states are NESTED under "no session yet" rather than being
  // siblings, so a loaded user cannot fall into either by construction. A
  // failure flag can outlive a good session — a later refresh failing, say —
  // and eclipsing a working session with an error is a worse lie than the flash
  // this block exists to prevent.
  if (!sessionUserLoaded) {
    // A transient failure never resolves on its own, so it gets a retry rather
    // than a spinner that would never stop. Distinct again from
    // `refreshErrorBanner`, the CHANNEL fetch failing: that one can still
    // render an editor around it, this one cannot decide whether to render one.
    if (sessionLoadFailed)
      return (
        <ListError title="We couldn’t load your account" onRetry={retrySession} />
      );
    return <Loader color={colors.MAIN_COLOR} size="large" isOverlay={true} />;
  }

  // A blocked ACCOUNT is not a blocked CHANNEL. `ChannelPolicy#update?` exempts
  // channel_admins, so a user whose own subscription has ended may still edit
  // channels they administer for a client — gating this route on the
  // account-wide flag alone locked them out of edits the server authorizes.
  //
  // `channelDetail.can_archive` is the per-channel verdict, under a legacy
  // name. Rails derives it from `ChannelPolicy#update?` (archiving is a channel
  // UPDATE — `PATCH /api/v2/channels/{id}` with `archived: true`), and the
  // subscription gate lives inside that same policy, so the flag already
  // carries the exemption. It is on this route's own payload: the `#show`
  // response (`GET /api/v2/channels/{id}`, GET_CHANNEL_SHAPE) emits it, and
  // `getChannelDetail` spreads the response through — so no list row is needed.
  if (channelManagementBlocked) {
    // No verdict yet. Deciding from the account-wide flag alone is what this
    // whole branch exists to avoid, so wait — but only while an answer is
    // actually coming. A failed detail fetch falls through to the banner
    // below, which carries its own Retry; spinning here would be permanent.
    if (!channelDetail && !refreshFailed)
      return <Loader color={colors.MAIN_COLOR} size="large" isOverlay={true} />;

    // Strict `!== true`: an absent flag on a blocked account stays blocked.
    if (channelDetail && channelDetail.can_archive !== true)
      return <SubscriptionRequiredNotice />;
  }

  // An initial fetch that failed leaves no channel to render at all, so the
  // banner is the only thing standing between the user and a blank page.
  if (!channelDetail) return <div>{refreshErrorBanner}</div>;
  let channel = { ...channelDetail };
  const instanceId = channel && channel.channel_theme_instance;
  const channelThemeInstance = channelThemeInstances[instanceId];

  if (channel && isPhotographer && channelThemeInstance) {
    channel = {
      ...channel,
      ...channelThemeInstance,
    };
  }

  const logoImage = isObject(channelFormLogo)
    ? makeUrl(channelFormLogo[0])
    : channel && urlFix(channel.big_logo_image_url);
  const overlayImage = isObject(channelFormBackground)
    ? makeUrl(channelFormBackground[0])
    : channel && urlFix(channel.overlay_image_url);
  const backgroundColor = isString(channelFormBackground)
    ? channelFormBackground
    : channel && channel.app_background_color;

  const bgColorChanged =
    isString(channelFormBackground) && !isObject(channelFormBackground);
  const overlayChanged =
    isObject(channelFormBackground) && !isString(channelFormBackground);

  const previewProps = {
    logoImage,
    overlayImage,
    backgroundColor,
    bgColorChanged,
    overlayChanged,
    galleryColor,
    brandForm,
    messageForm,
  };

  const initialChannel = Object.assign(
    {},
    {
      with_photo_filter:
        channel && channel.channel_theme_instance.with_photo_filter
          ? channel.channel_theme_instance.with_photo_filter
          : false,
      with_photo_filter_graphic: true,
      email_required: shareTypeFlag(channel, "email", "required"),
      email_enabled: shareTypeFlag(channel, "email", "enabled"),
      phone_required: shareTypeFlag(channel, "phone", "required"),
      phone_enabled: shareTypeFlag(channel, "phone", "enabled"),
      twitter_required: shareTypeFlag(channel, "twitter", "required"),
      twitter_enabled: shareTypeFlag(channel, "twitter", "enabled"),
      channel_theme_id:
        channel && channel.channel_theme_instance
          ? channel.channel_theme_instance.channel_theme_id
          : null,
      first_line_text:
        channel && channel.channel_theme_instance
          ? channel.channel_theme_instance.first_line_text
          : null,
      second_line_text:
        channel && channel.channel_theme_instance
          ? channel.channel_theme_instance.second_line_text
          : null,
      gallery_banner: 
      channel && channel.big_logo_image_file_name
        ? channel.big_logo_image_file_name
        : false,
    },
    { ...channel, client: channel.client ? channel.client.name : "" }
  );
  return (
    <div>
      {isFetching ||
        (isSaving && (
          <Loader color={colors.MAIN_COLOR} size="large" isOverlay={true} />
        ))}
      {refreshErrorBanner}
      <Forms
        form={form}
        channelId={channelId}
        title="Edit Channel"
        onSubmit={submitChannel}
        initialValues={initialChannel}
        channelThemes={channelThemes}
        themeId={themeId}
        edit={true}
        disabled={
          channel.event_date <= dayjs().format("YYYY-MM-DD") &&
          isPhotographer
            ? true
            : false
        }
        themeInstanceId={themeInstanceId}
        channelThemeInstances={channelThemeInstances}
        {...previewProps}
      />
    </div>
  );
};

const mapStateToProps = createStructuredSelector({
  channelDetail: (state) => state.channels.channelDetail,
  channelDetailError: (state) => state.channels.channelDetailError,
  refreshPending: (state) => state.channels.refreshPending,
  isFetching: (state) => state.channels.isFetching,
  channelThemes: selectChannelThemes(),
  isPhotographer: selectIsPhotographer(),
  channelManagementBlocked: selectChannelManagementBlocked(),
  sessionUserLoaded: selectSessionUserLoaded(),
  sessionLoadFailed: selectSessionLoadFailed(),
  channelThemeInstances: selectChannelThemeInstances(),
  isSaving: (state) => state.channels.isSaving,
});

const mapDispatchToProps = (dispatch, { params }) => ({
  fetchUpdatedDetail: (id) => dispatch(fetchUpdatedChannelDetailRequest(id)),
  fetchDetail: (id) => dispatch(fetchChannelDetailRequest(id)),
  retrySession: () => dispatch(fetchSession()),
  patchChannel: (data) =>
    dispatch(patchChannelRequest({ id: params.channelId, data })),
});

// withRouter is OUTSIDE connect on purpose: mapDispatchToProps reads
// :channelId from ownProps, so `params` has to be injected before connect
// runs. v7 injects no router props into a route `element`.
export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(EditChannel)
);
